import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./risk-score-field.i18n";

type RiskScoreFieldProps = {
  /** The chosen score as the Select's string value ("" = none yet). */
  value: string;
  onChange: (value: string) => void;
  /** Matrix scores of the collection's risk model. */
  scores: number[];
  /** The score of an existing portfolio is shown, not edited. */
  readOnly: boolean;
  /** Explains the choice below the Select. */
  hint: string;
};

/** Risk score of a portfolio (model or dynamic): chosen at creation, fixed after. */
export function RiskScoreField({ value, onChange, scores, readOnly, hint }: RiskScoreFieldProps) {
  const t = useT(T);

  if (readOnly) {
    return (
      <div className="space-y-2">
        <Label>{t.riskScore}</Label>
        <div className="p-3 border rounded bg-muted/50">
          <p className="font-medium">{value}</p>
          <p className="text-xs text-muted-foreground">{t.riskScoreCannotChange}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="riskScore">
        {t.riskScore} <span className="text-destructive">*</span>
      </Label>
      <Select value={value} onValueChange={onChange} disabled={scores.length === 0}>
        <SelectTrigger id="riskScore">
          <SelectValue placeholder={t.riskScorePlaceholder} />
        </SelectTrigger>
        <SelectContent>
          {scores.map((score) => (
            <SelectItem key={score} value={score.toString()}>
              {score}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {scores.length === 0 ? t.noAvailableScores : hint}
      </p>
    </div>
  );
}
